// src/pages/Admin/ReglementInscription/useReglementsInscriptionAdmin.js

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllReglementInscription,
  updateReglementInscription,
} from "../../../../services/reglementinscriptionServices";
import { getSettings } from "../../../../services/settingsServices";
import { getGroupsList } from "../../../../services/groupServices";
import {
  getAllDossiersInscription,
  updateDossierInscription,
} from "../../../../services/dossierServices";

import {
  CHUNK,
  calculateReglementTotal,
  extractArray,
  getAdherentName,
  getGroupsText,
  isReglementValidated,
  neighborsFromSeason,
  normalizeText,
} from "./reglement.helpers";

export default function useReglementsInscriptionAdmin() {
  const [reglements, setReglements] = useState([]);
  const [loading, setLoading] = useState(true);

  const [clubSettings, setClubSettings] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState("");

  const [groups, setGroups] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selectedSeason, setSelectedSeason] = useState("");
  const [passSportFilter, setPassSportFilter] = useState("");
  const [statusFilters, setStatusFilters] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");

  const [sortField, setSortField] = useState("name"); // name | total
  const [sortOrder, setSortOrder] = useState("asc");

  const [editingStatusId, setEditingStatusId] = useState(null);
  const [statusDraft, setStatusDraft] = useState("");
  const [savingStatusId, setSavingStatusId] = useState(null);

  const [visibleCount, setVisibleCount] = useState(CHUNK);
  const scrollRef = useRef(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(normalizeText(searchTerm));
    }, 250);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const fetchReglements = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getAllReglementInscription();
      setReglements(extractArray(response));
    } catch (error) {
      console.error("Erreur chargement règlements :", error);
      toast.error("Erreur lors du chargement des règlements.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchContext = useCallback(async () => {
    try {
      const [settingsResponse, groupsResponse] = await Promise.all([
        getSettings(),
        getGroupsList(),
      ]);

      const settingsRows = extractArray(settingsResponse);
      const settings = settingsRows?.[0] || null;
      const active = settings?.saison_active || "";

      setClubSettings(settings);
      setActiveSeason(active);
      setSeasons(neighborsFromSeason(active));
      setSelectedSeason(active || "");

      setGroups(extractArray(groupsResponse));
    } catch (error) {
      console.error("Erreur contexte règlements :", error);
      toast.error("Erreur lors du chargement des paramètres.");
    }
  }, []);

  const fetchInitialData = useCallback(async () => {
    await Promise.all([fetchReglements(), fetchContext()]);
  }, [fetchReglements, fetchContext]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const syncDossierValidateReglement = useCallback(
    async (adherentId, saison, statutReglement) => {
      try {
        if (!adherentId || !saison) return;

        const dossiersResponse = await getAllDossiersInscription();
        const dossiers = extractArray(dossiersResponse);

        const target = dossiers.find((dossier) => {
          const dossierAdherentId =
            dossier?.inscription_adherent_id || dossier?.Adherent?.id;
          const dossierSaison = dossier?.saison || dossier?.Adherent?.saison_active;

          return (
            String(dossierAdherentId) === String(adherentId) &&
            String(dossierSaison) === String(saison)
          );
        });

        if (!target?.id) {
          console.warn(
            "[Reglement] Aucun dossier trouvé pour",
            adherentId,
            saison
          );
          return;
        }

        await updateDossierInscription(target.id, {
          validate_reglement: isReglementValidated(statutReglement),
        });
      } catch (error) {
        console.error("Erreur synchronisation dossier :", error);
        toast.error(
          "Statut mis à jour, mais la synchronisation du dossier a échoué."
        );
      }
    },
    []
  );

  const saveStatus = useCallback(
    async (reglement, newStatus) => {
      const id = reglement?.id ?? reglement?.reglement_id;

      if (!id) {
        toast.error("ID du règlement introuvable.");
        return;
      }

      try {
        setSavingStatusId(id);

        await updateReglementInscription({
          id,
          statut_reglement: newStatus,
        });

        const adherentId = reglement?.Adherent?.id || reglement?.adherent_id;
        const saison = reglement?.saison || selectedSeason;

        await syncDossierValidateReglement(adherentId, saison, newStatus);

        toast.success("Statut mis à jour.");
        setEditingStatusId(null);
        setStatusDraft("");

        await fetchReglements();
      } catch (error) {
        console.error("Erreur mise à jour statut :", error);
        toast.error("Erreur lors de la mise à jour du statut.");
      } finally {
        setSavingStatusId(null);
      }
    },
    [fetchReglements, selectedSeason, syncDossierValidateReglement]
  );

  const passSportOptions = useMemo(() => {
    const values = reglements
      .map((reglement) => Number(reglement?.pass_sport))
      .filter((value) => Number.isFinite(value));

    return Array.from(new Set(values)).sort((a, b) => a - b);
  }, [reglements]);

  const filtered = useMemo(() => {
    const rows = reglements.filter((reglement) => {
      const adherent = reglement?.Adherent || {};

      const haystack = normalizeText(
        [
          adherent?.nom,
          adherent?.prenom,
          adherent?.email,
          reglement?.saison,
          reglement?.statut_reglement,
          reglement?.type_reglement,
          reglement?.observations,
          getGroupsText(reglement),
        ]
          .filter(Boolean)
          .join(" ")
      );

      const matchesSearch = !debouncedSearch || haystack.includes(debouncedSearch);

      const matchesSeason =
        !selectedSeason || String(reglement?.saison || "") === String(selectedSeason);

      const matchesPassSport =
        passSportFilter === ""
          ? true
          : passSportFilter === "nonzero"
            ? Number(reglement?.pass_sport) !== 0
            : Number(reglement?.pass_sport) === Number(passSportFilter);

      const matchesStatus =
        statusFilters.length === 0 ||
        statusFilters.includes(reglement?.statut_reglement);

      const matchesGroup =
        !selectedGroup ||
        (Array.isArray(adherent?.Groups) &&
          adherent.Groups.some(
            (group) => String(group?.id) === String(selectedGroup)
          ));

      return (
        matchesSearch &&
        matchesSeason &&
        matchesPassSport &&
        matchesStatus &&
        matchesGroup
      );
    });

    rows.sort((a, b) => {
      if (sortField === "total") {
        const totalA = calculateReglementTotal(a);
        const totalB = calculateReglementTotal(b);

        return sortOrder === "asc" ? totalA - totalB : totalB - totalA;
      }

      const nameA = getAdherentName(a);
      const nameB = getAdherentName(b);

      return sortOrder === "asc"
        ? nameA.localeCompare(nameB, "fr", { sensitivity: "base" })
        : nameB.localeCompare(nameA, "fr", { sensitivity: "base" });
    });

    return rows;
  }, [
    reglements,
    debouncedSearch,
    selectedSeason,
    passSportFilter,
    statusFilters,
    selectedGroup,
    sortField,
    sortOrder,
  ]);

  const totalAffiche = useMemo(() => {
    return filtered.reduce(
      (sum, reglement) => sum + calculateReglementTotal(reglement),
      0
    );
  }, [filtered]);

  const stats = useMemo(() => {
    return {
      total: reglements.length,
      affiches: filtered.length,
      regles: filtered.filter((r) => r?.statut_reglement === "Réglé").length,
      enCours: filtered.filter((r) =>
        ["En cours", "Partiellement réglé"].includes(r?.statut_reglement)
      ).length,
      nonRegles: filtered.filter((r) => r?.statut_reglement === "Non réglé").length,
      totalAffiche,
    };
  }, [reglements, filtered, totalAffiche]);

  const visibleReglements = filtered.slice(0, visibleCount);

  useEffect(() => {
    setVisibleCount(CHUNK);

    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [
    debouncedSearch,
    selectedSeason,
    passSportFilter,
    statusFilters,
    selectedGroup,
    sortField,
    sortOrder,
  ]);

  const onScroll = (event) => {
    const element = event.currentTarget;

    if (element.scrollTop + element.clientHeight >= element.scrollHeight - 220) {
      setVisibleCount((current) =>
        current >= filtered.length
          ? current
          : Math.min(current + CHUNK, filtered.length)
      );
    }
  };

  const toggleNameSort = () => {
    setSortField("name");
    setSortOrder((current) =>
      sortField === "name" && current === "asc" ? "desc" : "asc"
    );
  };

  const toggleTotalSort = () => {
    setSortField("total");
    setSortOrder((current) =>
      sortField === "total" && current === "asc" ? "desc" : "asc"
    );
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedSeason(activeSeason || "");
    setPassSportFilter("");
    setStatusFilters([]);
    setSelectedGroup("");
    setSortField("name");
    setSortOrder("asc");
  };

  return {
    state: {
      reglements,
      loading,
      clubSettings,
      seasons,
      activeSeason,
      groups,
      searchTerm,
      selectedSeason,
      passSportFilter,
      statusFilters,
      selectedGroup,
      passSportOptions,
      sortField,
      sortOrder,
      editingStatusId,
      statusDraft,
      savingStatusId,
      visibleCount,
      scrollRef,
      filtered,
      visibleReglements,
      totalAffiche,
      stats,
    },

    setters: {
      setSearchTerm,
      setSelectedSeason,
      setPassSportFilter,
      setStatusFilters,
      setSelectedGroup,
      setEditingStatusId,
      setStatusDraft,
    },

    handlers: {
      fetchReglements,
      fetchContext,
      fetchInitialData,
      saveStatus,
      syncDossierValidateReglement,
      toggleNameSort,
      toggleTotalSort,
      resetFilters,
      onScroll,
    },
  };
}